import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, FileDown, Activity, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { LoadingBlock } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/adminTestIds";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";

const TAB_MODULES = ["general", "branding", "security", "lms"];

export default function AdminPlatformSettingsPage() {
  const [settings, setSettings] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // module currently saving
  const { refresh: refreshSiteConfig } = useSiteConfig();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/platform-settings");
      const list = data.data.settings.filter((s) => TAB_MODULES.includes(s.module_name));
      setSettings(list);
      const v = {};
      list.forEach((s) => { v[s.setting_key] = s.setting_value; });
      setValues(v);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = {};
    settings.forEach((s) => {
      g[s.module_name] = g[s.module_name] || [];
      g[s.module_name].push(s);
    });
    return g;
  }, [settings]);

  const save = async (module) => {
    setSaving(module);
    try {
      const items = grouped[module] || [];
      const payload = {};
      items.forEach((s) => { payload[s.setting_key] = values[s.setting_key]; });
      await api.put("/admin/platform-settings", { settings: payload });
      toast.success(`${module.charAt(0).toUpperCase() + module.slice(1)} settings saved.`);
      // Broadcast to every consumer of SiteConfigContext (WhatsApp FAB,
      // footer contact line, support-phone link, etc.) so the new
      // value lands on already-open admin tabs without a hard refresh.
      refreshSiteConfig?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Platform Settings" breadcrumbs={["Platform", "Settings"]}>
        <LoadingBlock label="Loading settings…" />
      </DashboardLayout>
    );
  }

  const renderInput = (s) => {
    const testId = ADMIN.settingsInput(s.setting_key);
    if (s.setting_type === "boolean") {
      return (
        <Switch
          checked={!!values[s.setting_key]}
          onCheckedChange={(v) => setValues((cur) => ({ ...cur, [s.setting_key]: v }))}
          data-testid={testId}
        />
      );
    }
    if (s.setting_type === "range") {
      const min = Number(s.range_min ?? 0);
      const max = Number(s.range_max ?? 100);
      const step = Number(s.range_step ?? 1);
      const cur = Number(values[s.setting_key] ?? min);
      return (
        <div className="space-y-1.5">
          <div className="flex items-center gap-3">
            <input
              type="range" min={min} max={max} step={step} value={cur}
              onChange={(e) => setValues((c) => ({ ...c, [s.setting_key]: Number(e.target.value) }))}
              data-testid={testId}
              className="flex-1 accent-[#101876]"
            />
            <div className="inline-flex min-w-[60px] items-center justify-center rounded-md bg-[#101876] px-2 py-1 text-xs font-bold text-white">
              {cur}%
            </div>
          </div>
          <div className="flex justify-between text-[10px] text-slate-400">
            <span>{min}%</span><span>{max}%</span>
          </div>
        </div>
      );
    }
    if (s.setting_type === "number") {
      return (
        <Input type="number" value={values[s.setting_key] ?? ""} onChange={(e) => setValues((cur) => ({ ...cur, [s.setting_key]: Number(e.target.value) }))} data-testid={testId} />
      );
    }
    if (s.setting_type === "color") {
      return (
        <div className="flex items-center gap-3">
          <input type="color" value={values[s.setting_key] || "#000000"} onChange={(e) => setValues((cur) => ({ ...cur, [s.setting_key]: e.target.value }))} className="h-10 w-14 cursor-pointer rounded-md border border-slate-200" />
          <Input value={values[s.setting_key] ?? ""} onChange={(e) => setValues((cur) => ({ ...cur, [s.setting_key]: e.target.value }))} data-testid={testId} />
        </div>
      );
    }
    return (
      <Input value={values[s.setting_key] ?? ""} onChange={(e) => setValues((cur) => ({ ...cur, [s.setting_key]: e.target.value }))} data-testid={testId} />
    );
  };

  const humanKey = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <DashboardLayout title="Platform Settings" breadcrumbs={["Platform", "Settings"]}>
      <div className="max-w-4xl space-y-4" data-testid={ADMIN.settingsRoot}>
        {/* SOP download card — Super Admin only, restricted server-side */}
        <div className="rounded-2xl border border-slate-200 bg-gradient-to-r from-[#101876]/5 to-[#329112]/5 p-5 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">Platform Documentation</div>
            <h3 className="text-lg font-bold text-slate-900 mt-0.5">Standard Operating Procedures (SOP)</h3>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              A full operations handbook covering every LMS module — auth, courses, exams, storefront, billing, certificates, doubts, backups, and admin checklists. <b>Confidential — Super Admin only.</b>
            </p>
          </div>
          <button
            type="button"
            onClick={async () => {
              try {
                const resp = await api.get("/admin/sop/download", { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
                const a = document.createElement("a");
                a.href = url;
                const d = new Date().toISOString().slice(0, 10).replace(/-/g, "");
                a.download = `Steward-LMS-SOP-${d}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                toast.success("SOP PDF downloaded.");
              } catch (err) {
                toast.error(formatApiError(err));
              }
            }}
            className="shrink-0 inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c]"
            data-testid="admin-sop-download-btn"
          >
            <FileDown className="h-4 w-4" /> Download SOP (PDF)
          </button>
        </div>

        <PaymentHealthCard />

        <Tabs defaultValue="general">
          <TabsList className="bg-white border border-slate-200 rounded-lg p-1">
            {TAB_MODULES.map((m) => (
              <TabsTrigger key={m} value={m} className="capitalize data-[state=active]:bg-[#101876] data-[state=active]:text-white">{m}</TabsTrigger>
            ))}
          </TabsList>

          {TAB_MODULES.map((m) => (
            <TabsContent key={m} value={m}>
              <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">{m}</div>
                    <h3 className="text-lg font-bold text-slate-900">{humanKey(m)} settings</h3>
                  </div>
                  <button
                    onClick={() => save(m)}
                    disabled={saving === m}
                    className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70"
                    data-testid={`admin-settings-save-${m}`}
                  >
                    {saving === m ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Save {m}
                  </button>
                </div>
                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                  {(grouped[m] || []).map((s) => (
                    <div key={s.setting_key} className={s.setting_type === "text" ? "sm:col-span-2 space-y-1.5" : "space-y-1.5"}>
                      <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{humanKey(s.setting_key)}</Label>
                      {renderInput(s)}
                      {s.description && <div className="text-[11px] text-slate-500">{s.description}</div>}
                    </div>
                  ))}
                </div>
              </section>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

function PaymentHealthCard() {
  const [state, setState] = useState({ loading: true, data: null });
  const [checking, setChecking] = useState(false);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get("/admin/payment-health");
      setState({ loading: false, data: data.data });
    } catch (err) {
      setState({ loading: false, data: null });
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => { load(); }, []);

  const checkNow = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/admin/payment-health/check-now");
      const r = data.data;
      if (r.ok) toast.success(`Razorpay reachable (${r.latency_ms} ms)`);
      else toast.error(`Health probe failed: ${r.reason}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setChecking(false); }
  };

  const latest = state.data?.latest;
  const ok = latest?.ok;
  const badgeCls = ok
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-rose-50 text-rose-700 border-rose-200";
  const iconCls = ok ? "text-emerald-600" : "text-rose-600";
  const badgeText = latest
    ? (ok ? `Healthy · ${latest.latency_ms || 0} ms` : `${latest.reason || "unknown"}`)
    : "No probe yet";

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="payment-health-card">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-[#101876]" />
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#101876]">Payment gateway health</div>
          </div>
          <h3 className="text-base font-bold text-slate-900 mt-1">Razorpay auto-monitor</h3>
          <p className="text-xs text-slate-600 mt-1 leading-relaxed">
            Runs every hour. If Razorpay ever stops authenticating, every Super Admin receives an email so rotated keys are caught before a shopper does.
          </p>
        </div>
        <button type="button" onClick={checkNow} disabled={checking}
                className="shrink-0 inline-flex items-center gap-2 rounded-md border-2 border-[#101876] bg-white px-3 py-2 text-xs font-bold text-[#101876] hover:bg-[#101876]/5 disabled:opacity-60"
                data-testid="payment-health-check-now">
          {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {checking ? "Checking…" : "Check now"}
        </button>
      </div>

      {state.loading ? (
        <div className="mt-4 text-xs text-slate-500">Loading probe history…</div>
      ) : latest ? (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className={`rounded-lg border p-3 ${badgeCls}`}>
            <div className="flex items-center gap-2 text-xs font-bold">
              {ok ? <CheckCircle2 className={`h-4 w-4 ${iconCls}`} /> : <AlertTriangle className={`h-4 w-4 ${iconCls}`} />}
              {badgeText}
            </div>
            <div className="mt-1 text-[10px] font-mono opacity-75" data-testid="payment-health-message">
              {latest.message || "—"}
            </div>
            <div className="mt-1 text-[10px] opacity-60">
              Last check: {new Date(latest.created_at).toLocaleString()}
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Recent 10 probes</div>
            <div className="mt-2 flex gap-1 flex-wrap" data-testid="payment-health-history">
              {(state.data.history || []).slice(0, 10).map((h) => (
                <span key={h.id} title={`${h.reason} · ${h.latency_ms}ms · ${new Date(h.created_at).toLocaleString()}`}
                      className={`inline-block h-4 w-4 rounded ${h.ok ? "bg-emerald-500" : "bg-rose-500"}`} />
              ))}
              {(state.data.history || []).length === 0 && (
                <span className="text-[10px] text-slate-400">No probes yet — click Check now.</span>
              )}
            </div>
            {state.data.last_alert && (
              <div className="mt-2 text-[10px] text-slate-500">
                Last alert email: <b>{new Date(state.data.last_alert.created_at).toLocaleString()}</b> · sent to {state.data.last_alert.recipients_sent} admin(s)
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-4 text-xs text-slate-500">No probe recorded yet.</div>
      )}
    </div>
  );
}
